import { extractIdsFromCitation } from "./extractCitationIds";
import { extractHighlightIdsFromLinkText } from "./highlightIds";
import {
  getFirstFqlTickerFromMap,
  getTableCompanyEntities,
  isMarkdownSeparatorRow,
  splitMarkdownTableRow,
  stripInlineMarkup,
} from "./tableHelpers";
import type { Citation, OutputLink } from "../types";

export interface UuidSet {
  has(id: string): boolean;
}

export interface DocumentSourceItem {
  kind: "document";
  id: string;
  link: OutputLink;
  client: string;
  envelope: Citation;
  label: string;
  citedUuids: string[];
}

export interface TableSourceItem {
  kind: "table";
  id: string;
  markdown: string;
  client: string;
  label: string;
  metadata?: OutputLink["metadata"] & { ticker?: string };
  citedUuids: string[];
  envelope: Citation;
}

export type SourceItem = DocumentSourceItem | TableSourceItem;

type TableMetadata = OutputLink["metadata"] & { ticker?: string };

function resolveLinkLabel(
  link: OutputLink,
  envelope: Citation,
  clientLabels: Record<string, string>
): string {
  const fromMeta = link.metadata?.dataSource;
  if (fromMeta && typeof fromMeta === "string") return fromMeta;
  const fromEnvelope = envelope.metadata?.dataSource;
  if (fromEnvelope && typeof fromEnvelope === "string") return fromEnvelope;
  if (envelope.client && clientLabels[envelope.client]) return clientLabels[envelope.client];
  if (envelope.client) return envelope.client;
  return "Other";
}

function resolveEnvelopeLabel(
  envelope: Citation,
  clientLabels: Record<string, string>
): string {
  const fromMeta = envelope.metadata?.dataSource;
  if (fromMeta && typeof fromMeta === "string") return fromMeta;
  if (envelope.client && clientLabels[envelope.client]) return clientLabels[envelope.client];
  if (envelope.client) return envelope.client;
  return "Other";
}

function getColumnValue(
  headers: string[],
  values: string[],
  matcher: RegExp
): string | undefined {
  for (let i = 0; i < headers.length; i++) {
    if (!matcher.test(headers[i])) continue;
    const value = stripInlineMarkup(values[i] ?? "");
    if (value) return value;
  }
  return undefined;
}

function extractMarkdownTableMetadata(
  markdown: string,
  citedUuids: string[]
): Pick<TableMetadata, "companyName" | "ticker"> {
  const tableLines = markdown
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.startsWith("|"));
  if (tableLines.length < 2) return {};

  const headers = splitMarkdownTableRow(tableLines[0]).map(stripInlineMarkup);
  const rows = tableLines.slice(1).filter((line) => !isMarkdownSeparatorRow(line));
  if (rows.length === 0) return {};

  let selectedRow = rows[0];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (citedUuids.some((id) => id && row.indexOf(id) !== -1)) {
      selectedRow = row;
      break;
    }
  }

  const values = splitMarkdownTableRow(selectedRow);
  const [companyName] = getTableCompanyEntities(markdown, citedUuids);
  return {
    companyName,
    ticker: getColumnValue(headers, values, /^(ticker|ticker\s*symbol|symbol)$/i),
  };
}

function buildTableMetadata(
  envelope: Citation,
  markdown: string,
  citedUuids: string[],
  label: string
): TableMetadata {
  const metadata: TableMetadata = { ...(envelope.metadata ?? {}) };
  if (!metadata.dataSource) metadata.dataSource = label;

  const fqlTicker = getFirstFqlTickerFromMap(envelope.FQL ?? envelope.fql, citedUuids);
  if (!metadata.ticker && fqlTicker) metadata.ticker = fqlTicker;

  const markdownMetadata = extractMarkdownTableMetadata(markdown, citedUuids);
  if (!metadata.ticker && markdownMetadata.ticker) {
    metadata.ticker = markdownMetadata.ticker;
  }
  if (!metadata.companyName && markdownMetadata.companyName) {
    metadata.companyName = markdownMetadata.companyName;
  }

  return metadata;
}

// Relocated to ./highlightIds so extractCitationIds.ts can share the same
// parser without a circular import (sourceItems imports extractCitationIds).
// Re-exported here to preserve existing importers (citations/index.ts, tests).
export { extractHighlightIdsFromLinkText };

function extractLinkOwnedUuids(text: string | undefined): string[] {
  if (!text) return [];
  const ids: string[] = [];
  const seen: Record<string, true> = {};
  const add = (id: string | undefined | null): void => {
    if (!id) return;
    if (seen[id]) return;
    seen[id] = true;
    ids.push(id);
  };
  const highlights = extractHighlightIdsFromLinkText(text);
  for (let i = 0; i < highlights.length; i++) add(highlights[i]);
  const inline = /\bid\s*=\s*("([0-9a-fA-F-]{8,})"|'([0-9a-fA-F-]{8,})')/g;
  let m: RegExpExecArray | null;
  while ((m = inline.exec(text)) !== null) {
    add(m[2] ?? m[3] ?? null);
  }
  return ids;
}

function addOwnedUuid(uuids: string[], id: string | undefined | null): void {
  if (!id) return;
  if (uuids.indexOf(id) !== -1) return;
  uuids.push(id);
}

function dictionaryKeyCanBeCitationId(
  key: string,
  quotedUuids: UuidSet | undefined
): boolean {
  if (quotedUuids) return quotedUuids.has(key);
  return /^[0-9a-fA-F-]{8,}$/.test(key);
}

interface ExtractOptions {
  clientLabels?: Record<string, string>;
  quotedUuids?: UuidSet;
}

function intersect(uuids: string[], quoted: UuidSet | undefined): string[] {
  if (!quoted) return uuids.slice();
  const out: string[] = [];
  for (const id of uuids) {
    if (quoted.has(id)) out.push(id);
  }
  return out;
}

export function extractSourceItemsFromCitations(
  citations: Citation[] | undefined,
  options: ExtractOptions = {}
): SourceItem[] {
  if (!citations || citations.length === 0) return [];
  const clientLabels = options.clientLabels ?? {};
  const quotedUuids = options.quotedUuids;

  const items: SourceItem[] = [];
  const seenIndex = new Map<string, number>();

  for (const envelope of citations) {
    if (!envelope || typeof envelope !== "object") continue;
    const type = envelope.citationType ?? envelope.citation_type;
    const data = envelope.citationData ?? envelope.citation_data;

    if (type === "Markdown" && typeof data === "string") {
      const id = `markdown:${envelope.client ?? "?"}:${cheapHash(data)}`;
      if (seenIndex.has(id)) continue;
      const allUuids = extractIdsFromCitation(envelope);
      const keptUuids = intersect(allUuids, quotedUuids);
      if (quotedUuids && keptUuids.length === 0) continue;
      const label = resolveEnvelopeLabel(envelope, clientLabels);
      seenIndex.set(id, items.length);
      items.push({
        kind: "table",
        id,
        markdown: data,
        client: envelope.client ?? "",
        label,
        metadata: buildTableMetadata(envelope, data, keptUuids, label),
        citedUuids: keptUuids,
        envelope,
      });
      continue;
    }

    if (type === "Dictionary" && data && typeof data === "object" && !Array.isArray(data)) {
      const linkEntries = Object.entries(data as Record<string, OutputLink>);

      for (const [key, link] of linkEntries) {
        if (!link || typeof link !== "object") continue;
        const url = link.link || key;
        const id = `document:${envelope.client ?? "?"}:${url || cheapHash(link.text ?? "")}`;

        // Keep filtering per link so sibling documents from the same envelope do not inherit citations.
        const ownedUuids = extractLinkOwnedUuids(link.text);
        if (dictionaryKeyCanBeCitationId(key, quotedUuids)) {
          addOwnedUuid(ownedUuids, key);
        }
        const keptOwned = intersect(ownedUuids, quotedUuids);

        if (quotedUuids) {
          if (keptOwned.length === 0) continue;
        }

        const existingIdx = seenIndex.get(id);
        if (existingIdx !== undefined) {
          const existing = items[existingIdx];
          if (existing && existing.kind === "document") {
            mergeIntoDocumentItem(existing, keptOwned);
          }
          continue;
        }

        seenIndex.set(id, items.length);
        items.push({
          kind: "document",
          id,
          link,
          client: envelope.client ?? "",
          envelope,
          label: resolveLinkLabel(link, envelope, clientLabels),
          citedUuids: keptOwned.slice(),
        });
      }
      continue;
    }
  }

  return items;
}

function mergeIntoDocumentItem(
  existing: DocumentSourceItem,
  incomingCitedUuids: string[]
): void {
  if (incomingCitedUuids.length === 0) return;

  const existingSet: Record<string, true> = {};
  for (let i = 0; i < existing.citedUuids.length; i++) {
    existingSet[existing.citedUuids[i]] = true;
  }
  let added = false;
  for (let i = 0; i < incomingCitedUuids.length; i++) {
    const uid = incomingCitedUuids[i];
    if (!uid || existingSet[uid]) continue;
    existingSet[uid] = true;
    existing.citedUuids.push(uid);
    added = true;
  }
  if (!added) return;

  const existingText = existing.link.text;
  if (
    typeof existingText === "string" &&
    HIGHLIGHT_IDS_ATTR_RE.test(existingText)
  ) {
    const rewritten = rewriteHighlightIds(existingText, existing.citedUuids);
    if (rewritten !== existingText) {
      existing.link = { ...existing.link, text: rewritten };
    }
  }
}

const HIGHLIGHT_IDS_ATTR_RE = /data-highlight-ids\s*=\s*("[^"]*"|'[^']*')/i;

function rewriteHighlightIds(text: string, uuids: string[]): string {
  const idsAttr = uuids.join(" ");
  return text.replace(HIGHLIGHT_IDS_ATTR_RE, () => `data-highlight-ids="${idsAttr}"`);
}

export function extractSourceItemsFromPools(
  pools: Array<Citation[] | undefined>,
  options: ExtractOptions = {}
): SourceItem[] {
  const merged: Citation[] = [];
  for (const pool of pools) {
    if (!Array.isArray(pool)) continue;
    for (const c of pool) {
      if (c) merged.push(c);
    }
  }
  return extractSourceItemsFromCitations(merged, options);
}

function cheapHash(value: string): string {
  let h = 5381;
  for (let i = 0; i < value.length; i++) {
    h = (h * 33) ^ value.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}
